import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Image,
  Input,
  message,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
} from 'antd';
import type { TableColumnType } from 'antd';
import BatchApproveDialog from './BatchApproveDialog';
import RejectDialog from './RejectDialog';
import HistoryDialog, { HistoryDialogRef } from './HistoryDialog';
import './index.css';
import { useQuery } from '@tanstack/react-query';
import {
  batchApproveArtwork,
  batchRejectArtwork,
  getArtworkList,
  getReviewStatus,
  getClassifications,
} from '@/api/contentReview';
import {
  Artwork,
  ArtworkListResponse,
  ArtworkListSearchParams,
} from '@/types/contentReview';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import dayjs from 'dayjs';
import { validateText } from '@/utils/validate';
import { FORMAT_TIME } from '@/types/common';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';

// 接口文档地址
// https://supptech.sg.larksuite.com/wiki/Nobsw0FhdilBpak9V2nlUc29gxh

export const CONTENT_REVIEW_IDS_KEY = 'content_review_ids';
export const CONTENT_REVIEW_INDEX_KEY = 'content_review_index';

const options = [{ label: 'No', value: '0' }, { label: 'Yes', value: '1' }];

const ContentReview: React.FC = () => {
  const [form] =
    Form.useForm<
      Omit<ArtworkListSearchParams, 'currentPage' | 'pageSize' | 'reviewStatus'>
    >();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [batchApproveDialogShow, setBatchApproveDialogShow] = useState(false);
  const [batchRejectDialogShow, setBatchRejectDialogShow] = useState(false);
  const historyDialogRef = useRef<HistoryDialogRef>(null);
  const allowBatchOperation = selectedKeys.length > 0;
  const isMobile = useAtomValue(isMobileAtom);
  const navigate = useNavigate();
  const pageSize = isMobile ? 10 : 20;
  const [page, setPage] = useState(1);
  const [tabKey, setTabKey] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const formValues: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (key === 'channelDictId') {
        formValues[key] = parseInt(value);
      } else {
        formValues[key] = value;
      }
    });
    form.setFieldsValue(formValues);
    const page = searchParams.get('page') || '1';
    setPage(parseInt(page));
  }, [searchParams, form]);

  const { data: reviewStatus } = useQuery({
    queryKey: ['reviewStatus'],
    queryFn: async () => {
      const resp = await getReviewStatus();
      if (resp.status !== 200) {
        return [];
      }
      const tabs = resp.data
        .map((item) => ({
          key: item.dictKey.toString(),
          label: item.dictDesc,
        }))
        .sort((a, b) => parseInt(a.key) - parseInt(b.key));
      const activeTab = searchParams.get('reviewKey') || tabs[0].key;
      setTabKey(activeTab);
      return tabs;
    },
  });

  const { data: classifications } = useQuery({
    queryKey: ['classifications'],
    queryFn: async () => {
      const resp = await getClassifications();
      return resp.data;
    },
  });

  const { data, isFetching, refetch } = useQuery<ArtworkListResponse>({
    queryKey: ['artworkList', page, tabKey],
    queryFn: async () => {
      const formValues = form.getFieldsValue();
      const timeRange = formValues.timeRange;
      const params: ArtworkListSearchParams = {
        currentPage: page,
        pageSize,
        reviewKey: parseInt(tabKey),
        ...formValues,
      };

      // 删除 timeRange，使用 createTimeStart 和 createTimeEnd
      delete params.timeRange;
      // 如果 channelDictId 为空，删除该字段
      if (!params.channelDictId) {
        delete params.channelDictId;
      }
      if (timeRange?.length === 2) {
        params.createTimeStart = timeRange[0].valueOf().toString();
        params.createTimeEnd = timeRange[1].valueOf().toString();
      }

      const resp = await getArtworkList(params);
      return resp.data;
    },
    enabled: !!tabKey,
  });

  const checkArtworkIds = () => {
    if (!form.getFieldValue('artworkIds')) {
      return true;
    }
    const artworkIds = form.getFieldValue('artworkIds') as string;
    if (!validateText(artworkIds, true)) {
      messageApi.error('Please use English commas to separate artwork IDs');
      return false;
    }
    // 检查数量是否超过 10
    const artworkIdsArray = artworkIds.split(',');
    if (artworkIdsArray.length > 10) {
      messageApi.error('The number of artwork IDs cannot exceed 10');
      return false;
    }
    return true;
  };

  const checkTags = () => {
    if (!form.getFieldValue('tags')) {
      return true;
    }
    const tags = form.getFieldValue('tags') as string;
    if (!validateText(tags, true)) {
      messageApi.error('Please use English commas to separate tags');
      return false;
    }
    // 检查数量是否超过 20
    const tagsArray = tags.split(',');
    if (tagsArray.length > 20) {
      messageApi.error('The number of tags cannot exceed 20');
      return false;
    }
    return true;
  };

  const onFinish = () => {
    if (!checkArtworkIds() || !checkTags()) {
      return;
    }
    const params = new URLSearchParams();
    const values = form.getFieldsValue();
    const timeRange = values.timeRange;

    // 处理时间范围
    if (timeRange?.length === 2) {
      params.set('createTimeStart', timeRange[0].valueOf().toString());
      params.set('createTimeEnd', timeRange[1].valueOf().toString());
    }

    // 处理其他参数
    Object.entries(values).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        key !== 'timeRange'
      ) {
        // 对于 channelDictId，只有当它有值时才传递
        if (key === 'channelDictId') {
          if (value) {
            params.set(key, value.toString());
          }
        } else {
          params.set(key, value.toString());
        }
      }
    });
    params.set('page', '1');
    setSearchParams(params);
    if (page !== 1) {
      setPage(1);
      return;
    }
    void refetch();
  };

  const handleTabChange = useCallback(
    (key: string) => {
      setPage(1);
      setTabKey(key);
      const params = new URLSearchParams(searchParams);
      params.set('page', '1');
      params.set('reviewKey', key);
      setSearchParams(params);
    },
    [setPage, setTabKey, setSearchParams, searchParams]
  );

  const columns: TableColumnType<Artwork>[] = [
    {
      title: 'Artwork ID',
      dataIndex: 'artworkId',
      key: 'artworkId',
    },
    {
      title: 'SUPP ID',
      key: 'suppId',
      render: (_, record) => record.suppId || '-',
    },
    {
      title: 'Username',
      key: 'userName',
      render: (_, record) => record.userName || '-',
    },
    {
      title: 'User Type',
      key: 'userType',
      render: (_, record) => (record.isArtist ? 'Artist' : 'Regular User'),
    },
    {
      title: 'Artwork',
      dataIndex: 'artwork',
      key: 'artwork',
      width: '120px',
      render: (_, record) => {
        if (!record.imageUrlList || record.imageUrlList.length === 0) {
          return <div>-</div>;
        }
        return (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {record.imageUrlList.slice(0, 3).map((url, index) => (
              <Image
                key={index}
                src={url}
                width={50}
                height={50}
                loading="lazy"
                placeholder={
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f5f5f5',
                      borderRadius: '4px',
                    }}
                  >
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      Loading
                    </span>
                  </div>
                }
                style={{
                  objectFit: 'cover',
                  borderRadius: '4px',
                }}
                preview={{
                  src: url,
                  mask:
                    record.imageUrlList.length > 1 ? (
                      <div style={{ fontSize: '12px' }}>
                        {index === 2 && record.imageUrlList.length > 3
                          ? `+${record.imageUrlList.length - 3}`
                          : 'Preview'}
                      </div>
                    ) : undefined,
                }}
              />
            ))}
          </div>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 150,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (_, record) => {
        return (
          <div style={{ width: 300, textWrap: 'wrap' }}>
            {record.description || '-'}
          </div>
        );
      },
    },
    {
      title: 'Sensitive Words',
      key: 'bannedWords',
      render: (_, record) => record.bannedWords || '-',
    },
    {
      title: 'Sensitive Image',
      key: 'bannedImage',
      render: (_, record) => (record.bannedImageFlag ? 'Yes' : 'No'),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: () => {
        return (
          <Space size="small" direction="vertical">
            <span>-</span>
            {/* <Tag color="blue">{record.categoryFirst}</Tag>
            <Tag color="green">{record.categorySecond}</Tag> */}
          </Space>
        );
      },
    },
    {
      title: 'Tag',
      dataIndex: 'tags',
      key: 'tags',
      render: (_, record) => {
        return <span>{record.tags || '-'}</span>;
      },
    },
    {
      title: 'Review Status',
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
    },
    {
      title: 'Review time',
      dataIndex: 'reviewTime',
      key: 'reviewTime',
      render: (_, record) => {
        return record.reviewTime ? <span>{dayjs(record.reviewTime).format(FORMAT_TIME)}</span> : '-';
      },
    },
    {
      title: 'Operator',
      dataIndex: 'reviewUser',
      key: 'reviewUser',
    },
    {
      title: 'Operation',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record, index) => (
        <Space size="small" direction="vertical">
          <Button type="link" onClick={() => handleReviewClick(index)}>
            Review
          </Button>
          <Button
            type="link"
            onClick={() => handleHistoryClick(record.artworkId)}
          >
            History
          </Button>
        </Space>
      ),
    },
  ];

  const handleReviewClick = useCallback(
    (index: number) => {
      const ids = data?.records.map((item) => item.artworkId) || [];
      const searchParams = new URLSearchParams();
      searchParams.set(CONTENT_REVIEW_IDS_KEY, ids.join(','));
      searchParams.set(CONTENT_REVIEW_INDEX_KEY, index.toString());
      navigate(`/content-review/detail?${searchParams.toString()}`);
    },
    [navigate, data]
  );

  const handleHistoryClick = useCallback((id: string) => {
    historyDialogRef.current?.show(id);
  }, []);

  const handleBatchApproveClick = useCallback(async () => {
    const resp = await batchApproveArtwork({
      artworkIdList: selectedKeys,
      approveKeys: '111',
      reviewRemark: '',
    });
    if (resp.status !== 200) {
      messageApi.error('Batch approve failed');
      return;
    }
    messageApi.success('Batch approve success');
    setSelectedKeys([]);
    void refetch();
  }, [messageApi, refetch, selectedKeys]);

  const handleBatchApproveDialogClose = useCallback(() => {
    setBatchApproveDialogShow(false);
  }, []);

  const handleBatchApproveDialogOk = useCallback(
    async (reason: string, remark: string) => {
      const resp = await batchApproveArtwork({
        artworkIdList: selectedKeys,
        approveKeys: reason,
        reviewRemark: remark,
      });
      if (resp.status !== 200) {
        messageApi.error('Batch approve failed');
        return;
      }
      messageApi.success('Batch approve success');
      setSelectedKeys([]);
      void refetch();
    },
    [messageApi, refetch, selectedKeys]
  );

  const handleBatchRejectClick = useCallback(() => {
    setBatchRejectDialogShow(true);
  }, []);

  const handleBatchRejectDialogClose = useCallback(() => {
    setBatchRejectDialogShow(false);
  }, []);

  const handleBatchRejectDialogOk = useCallback(
    async (
      image: number[],
      title: number[],
      description: number[],
      remark: string
    ) => {
      const resp = await batchRejectArtwork({
        artworkIdList: selectedKeys,
        imageRejectKeys: image.join(','),
        titleRejectKeys: title.join(','),
        descriptionRejectKeys: description.join(','),
        reviewRemark: remark,
      });
      if (resp.status !== 200) {
        messageApi.error('Batch reject failed');
        return;
      }
      messageApi.success('Batch reject success');
      setSelectedKeys([]);
      void refetch();
    },
    [messageApi, refetch, selectedKeys, setSelectedKeys]
  );

  const handleSelectAll = useCallback(() => {
    const keys = data?.records.map((item) => item.artworkId);
    setSelectedKeys(keys || []);
  }, [data]);

  const { RangePicker } = DatePicker;

  const handleReset = () => {
    const params = new URLSearchParams();
    setSearchParams(params);
    setPage(1);
    form.resetFields();
    refetch();
  }

  return (
    <div className="content-review-container">
      {contextHolder}
      <Form form={form} name="searchForm" onFinish={onFinish} size="small">
        <Row gutter={[8, 8]} style={{ width: '100%' }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="artworkIds"
              label="Artwork ID"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input placeholder="Enter Artwork ID" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="title"
              label="Title"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input placeholder="Enter title" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="userNumber"
              label="SUPP ID"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input placeholder="Enter SUPP ID" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="tags"
              label="Tag"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input placeholder="Enter tags" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="reviewUser"
              label="Operator"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input placeholder="Enter operator" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="channelDictId"
              label="Category"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                allowClear
                placeholder="Select category"
                options={classifications?.map((item) => ({
                  label: item.remark,
                  value: item.channelDictId,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="timeRange"
              label="Date"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="ifIncludeSensitiveWords"
              label="Sensitive Words"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                allowClear
                placeholder="Select"
                options={options}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="ifIncludeSensitiveImage"
              label="Sensitive Image"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                allowClear
                placeholder="Select"
                options={options}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="ifInternalUser"
              label="If Internal User"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                allowClear
                placeholder="Select"
                options={options}
              />
            </Form.Item>
          </Col>
          
          <Col span={24}>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              borderTop: '1px solid #f0f0f0',
              marginTop: '8px',
              paddingTop: '16px'
            }}>
              <Space size="middle">
                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                  Reset
                </Button>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                  Search
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Form>

      <div className="table-container">
        <div className="table-header">
          <Tabs
            type="card"
            size="small"
            items={reviewStatus || []}
            activeKey={tabKey}
            onChange={handleTabChange}
            className="tabs-container"
          />
          {!isMobile && (
            <Space size="middle">
              <Popconfirm
                title="Are you sure you want to approve these items?"
                onConfirm={handleBatchApproveClick}
              >
                <Button
                  type="primary"
                  disabled={!allowBatchOperation}
                  icon={<CheckOutlined />}
                >
                  Approve
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Are you sure you want to reject these items?"
                onConfirm={handleBatchRejectClick}
                okType="danger"
              >
                <Button
                  type="primary"
                  danger
                  disabled={!allowBatchOperation}
                  icon={<CloseOutlined />}
                >
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          )}
        </div>
        <Table
          size="small"
          loading={isFetching}
          rowKey="artworkId"
          columns={columns}
          dataSource={data?.records || []}
          scroll={{ x: 'max-content' }}
          rowSelection={
            isMobile
              ? undefined
              : {
                selectedRowKeys: selectedKeys,
                type: 'checkbox',
                fixed: true,
                onChange: (selectedRowKeys) => {
                  setSelectedKeys(selectedRowKeys as string[]);
                },
              }
          }
          pagination={false}
        />

        <Flex
          className="table-pagination"
          justify="space-between"
          style={{ flexDirection: isMobile ? 'row-reverse' : 'row' }}
        >
          {!isMobile && (
            <Space>
              <Button type="primary" danger onClick={handleSelectAll}>
                Select All
              </Button>
              <Button onClick={() => setSelectedKeys([])}>Deselect</Button>
            </Space>
          )}

          <Pagination
            size={isMobile ? 'small' : 'default'}
            total={data?.total}
            pageSize={pageSize}
            current={page}
            onChange={(page) => {
              setPage(page);
              const params = new URLSearchParams(searchParams);
              params.set('page', page.toString());
              setSearchParams(params);
            }}
            showSizeChanger={false}
          />
        </Flex>
      </div>

      <BatchApproveDialog
        isOpen={batchApproveDialogShow}
        onClose={handleBatchApproveDialogClose}
        onConfirm={handleBatchApproveDialogOk}
      />

      <RejectDialog
        isOpen={batchRejectDialogShow}
        onClose={handleBatchRejectDialogClose}
        onConfirm={handleBatchRejectDialogOk}
      />

      <HistoryDialog ref={historyDialogRef} />
    </div>
  );
};

export default ContentReview;
